import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const t = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour12: false }) : "—";

export default function Attendance() {
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [team, setTeam] = useState([]);
  const [archived, setArchived] = useState(true);
  const [historySince, setHistorySince] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/attendance-day", { params: { date } })
      .then((r) => {
        setTeam(r.data.users);
        setArchived(r.data.archived);
        setHistorySince(r.data.history_since);
      })
      .catch(() => toast.error("Could not load attendance — the field server may be waking up, try again shortly"))
      .finally(() => setLoading(false));
  }, [date]);

  const present = team.filter((u) => u.attendance).length;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="attendance-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Daily Records</p>
          <h1 className="font-heading font-black tracking-tight text-3xl">Attendance</h1>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <p className="text-sm text-slate-500">
            <span className="font-mono-data font-semibold text-slate-950">{present}</span> / {team.length} present
          </p>
          <input
            type="date"
            data-testid="attendance-date-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border border-slate-300 rounded-md px-3 py-2 text-sm font-mono-data focus:outline-none focus:ring-2 focus:ring-[#007AFF]"
          />
        </div>
      </div>

      {!loading && !archived && (
        <div className="flex items-start gap-2 border border-amber-300 bg-amber-50 text-amber-800 rounded-md px-4 py-3 mb-4 text-sm" data-testid="attendance-no-archive-notice">
          <Info size={16} className="mt-0.5 shrink-0" />
          <p>
            No archived attendance for this date. The mobile app's backend doesn't provide historical records, so this console records daily history itself
            {historySince ? ` — records available from ${historySince} onward.` : " — recording starts today."}
          </p>
        </div>
      )}

      <div className="border border-slate-200 rounded-md overflow-hidden max-w-full">
        <table className="w-full text-sm" data-testid="attendance-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left">
              {["Employee", "ID", "Status", "Login", "Logout", "Hours", "Distance (km)", "Stops", "Max Speed"].map((h) => (
                <th key={h} className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {team.map((u) => {
              const a = u.attendance;
              return (
                <tr key={u.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`attendance-row-${u.employee_id}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 font-mono-data text-xs text-slate-500">{u.employee_id}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-sm uppercase tracking-wider ${a ? "bg-blue-50 text-[#007AFF]" : "bg-slate-100 text-slate-500"}`}>
                      {a ? "Present" : "Absent"}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono-data text-xs">{t(a?.login_time)}</td>
                  <td className="px-4 py-3 font-mono-data text-xs">{t(a?.logout_time)}</td>
                  <td className="px-4 py-3 font-mono-data text-xs">{a?.working_hours ?? "—"}</td>
                  <td className="px-4 py-3 font-mono-data text-xs">{a?.distance_km ?? "—"}</td>
                  <td className="px-4 py-3 font-mono-data text-xs">{a?.stops ?? "—"}</td>
                  <td className="px-4 py-3 font-mono-data text-xs">{a?.max_speed ?? "—"}</td>
                </tr>
              );
            })}
            {!loading && team.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No employees found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
